// pages/Dashboard.jsx  –  Enhanced Analytics Dashboard
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Camera, Users, AlertTriangle, Bell, Clock, RefreshCw,
  BarChart3, Activity, ShieldAlert, UserPlus,
  Layers, TrendingUp, X, CheckCircle, Zap, Eye,
  Radio, Thermometer, Target, ChevronUp, ChevronDown,
  Minus, Shield
} from "lucide-react";
import {
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, PieChart, Pie,
  RadialBarChart, RadialBar
} from "recharts";
import { Empty, Spin } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import axios from "../../utils/axiosDash";

dayjs.extend(relativeTime);

// ── HELPERS ──────────────────────────────────────────────────────────────

const getDensityMeta = (count) => {
  if (count === 0) return { label: "Empty",    dot: "bg-slate-400",   badge: "bg-slate-100 text-slate-500" };
  if (count <= 3)  return { label: "Low",      dot: "bg-emerald-500", badge: "bg-emerald-50 text-emerald-700" };
  if (count <= 6)  return { label: "Medium",   dot: "bg-amber-500",   badge: "bg-amber-50 text-amber-700" };
  if (count <= 10) return { label: "High",     dot: "bg-violet-500",  badge: "bg-violet-50 text-violet-700" };
  return             { label: "Critical",  dot: "bg-red-500",     badge: "bg-red-50 text-red-700" };
};

const getHeatColor = (value, max) => {
  if (!max || value === 0) return { bg: "#F1F5F9", text: "#94A3B8" };
  const ratio = value / max;
  if (ratio < 0.2)  return { bg: "#ECFDF5", text: "#059669" };
  if (ratio < 0.4)  return { bg: "#D1FAE5", text: "#047857" };
  if (ratio < 0.6)  return { bg: "#FEF3C7", text: "#D97706" };
  if (ratio < 0.8)  return { bg: "#FED7AA", text: "#C2410C" };
  return               { bg: "#FEE2E2", text: "#DC2626" };
};

const getCameraStatus = (cam) => {
  const alertRate = cam.total_events > 0 ? (cam.alerts / cam.total_events) : 0;
  if (alertRate > 0.3)  return { label: "Critical", color: "text-red-600",    bg: "bg-red-50",     dot: "bg-red-500",     border: "border-red-200" };
  if (alertRate > 0.1)  return { label: "Warning",  color: "text-amber-600",  bg: "bg-amber-50",   dot: "bg-amber-500",   border: "border-amber-200" };
  if (cam.total_events > 0) return { label: "Active",   color: "text-emerald-600",bg: "bg-emerald-50", dot: "bg-emerald-500", border: "border-emerald-200" };
  return                     { label: "Idle",     color: "text-slate-400",  bg: "bg-slate-50",   dot: "bg-slate-300",   border: "border-slate-200" };
};

const PALETTE = ["#3D63DD", "#0EA5A8", "#F59E0B", "#8B5CF6", "#10B981", "#EF4444"];
const HOURS   = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, "0")}:00`);

// ── CHART TOOLTIP ──────────────────────────────────────────────────────────

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 rounded-xl px-4 py-3 shadow-2xl border border-white/10 min-w-[140px]">
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="flex items-center gap-2 text-xs text-slate-300 my-0.5">
          <span className="w-2 h-2 rounded-full inline-block" style={{ background: p.color || p.fill }} />
          <span>{p.name}:</span>
          <strong className="text-white">{p.value}</strong>
        </p>
      ))}
    </div>
  );
};

// ── KPI CARD ──────────────────────────────────────────────────────────────

const KPICard = ({ title, value, icon: Icon, colorClass, bgClass, sub, trend }) => (
  <div className="relative overflow-hidden bg-white rounded-2xl border border-slate-200 px-6 py-5 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all duration-200">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400 mb-1.5">{title}</p>
        <p className="text-3xl font-black text-slate-900 leading-none tracking-tight">{value ?? "—"}</p>
        {sub && <p className="text-xs text-slate-400 mt-2">{sub}</p>}
      </div>
      <div className="flex flex-col items-end gap-2">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${bgClass}`}>
          <Icon size={20} className={colorClass} strokeWidth={2.1} />
        </div>
        {trend !== undefined && (
          <span className={`text-[10px] font-bold flex items-center gap-0.5 ${trend > 0 ? "text-red-500" : trend < 0 ? "text-emerald-500" : "text-slate-400"}`}>
            {trend > 0 ? <ChevronUp size={11} /> : trend < 0 ? <ChevronDown size={11} /> : <Minus size={11} />}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
    </div>
  </div>
);

// ── METRIC CARD ──────────────────────────────────────────────────────────────

const MetricCard = ({ title, value, icon: Icon, colorClass, bgClass, sub, trend }) => (
  <div className="relative overflow-hidden bg-white rounded-2xl border border-slate-200 px-6 py-5 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all duration-200">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400 mb-1.5">{title}</p>
        <p className="text-3xl font-black text-slate-900 leading-none tracking-tight">{value ?? "—"}</p>
        {sub && <p className="text-xs text-slate-400 mt-2">{sub}</p>}
      </div>
      <div className="flex flex-col items-end gap-2">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${bgClass}`}>
          <Icon size={20} className={colorClass} strokeWidth={2.1} />
        </div>
        {trend !== undefined && (
          <span className={`text-[10px] font-bold flex items-center gap-0.5 ${trend > 0 ? "text-red-500" : trend < 0 ? "text-emerald-500" : "text-slate-400"}`}>
            {trend > 0 ? <ChevronUp size={11} /> : trend < 0 ? <ChevronDown size={11} /> : <Minus size={11} />}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
    </div>
  </div>
);

// ── SECTION HEADER ────────────────────────────────────────────────────────

const SectionHeader = ({ icon: Icon, iconClass, iconBg, title, sub, right }) => (
  <div className="flex items-center justify-between gap-4 mb-5">
    <div className="flex items-center gap-3">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
        <Icon size={16} className={iconClass} strokeWidth={2.2} />
      </div>
      <div>
        <p className="text-sm font-bold text-slate-800 leading-tight">{title}</p>
        {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </div>
    {right}
  </div>
);

// ── ALERT ROW ──────────────────────────────────────────────────────────────

const AlertRow = ({ alert, onClick }) => {
  const count   = alert.personsVisible || alert.person_count || 0;
  const isAlert = alert.alert === true;
  const { label, dot, badge } = getDensityMeta(count);
  return (
    <div
      onClick={() => alert.image_url && onClick(alert)}
      className={`flex items-center gap-3 px-4 py-3 border-b border-slate-100 transition-colors last:border-b-0
        ${alert.image_url ? "cursor-pointer" : "cursor-default"}
        ${isAlert ? "bg-red-50 border-l-2 border-l-red-400" : "hover:bg-slate-50"}`}
    >
      <div className="w-14 h-11 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100 border border-slate-200 flex items-center justify-center">
        {alert.image_url ? (
          <img src={alert.image_url} alt="event" className="w-full h-full object-cover"
            onError={e => { e.target.style.display = "none"; }} />
        ) : (
          <Camera size={15} className="text-slate-400" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <span className="text-sm font-bold text-slate-800 truncate">
            {alert.camera_name || `Cam ${alert.camera_id}`}
          </span>
          <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${badge}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
            {label} ({count})
          </span>
          {isAlert && (
            <span className="text-[10px] font-bold bg-red-100 text-red-600 px-2 py-0.5 rounded-full">⚠ ALERT</span>
          )}
        </div>
      </div>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isAlert ? "bg-red-100" : "bg-slate-100"}`}>
        {isAlert ? <AlertTriangle size={13} className="text-red-500" /> : <UserPlus size={13} className="text-slate-500" />}
      </div>
    </div>
  );
};

// ── LIGHTBOX ──────────────────────────────────────────────────────────────

const Lightbox = ({ open, onClose, alert }) => {
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  if (!open || !alert) return null;
  return (
    <div onClick={onClose}
      className="fixed inset-0 z-[2000] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 animate-[fadeIn_0.18s_ease]">
      <div onClick={e => e.stopPropagation()}
        className="bg-slate-900 rounded-2xl overflow-hidden max-w-[780px] w-full shadow-[0_40px_100px_rgba(0,0,0,0.6)] border border-white/10 animate-[slideUp_0.22s_cubic-bezier(0.34,1.56,0.64,1)]">
        <img src={alert.image_url} alt="alert"
          className="w-full max-h-[70vh] object-contain block bg-black"
          onError={e => { e.target.alt = "Image unavailable"; }} />
        <div className="px-5 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="flex items-center gap-1.5 text-xs text-slate-400">
              <Camera size={13} className="text-teal-400" /> {alert.camera_name || alert.camera_id}
            </span>
          </div>
          <button onClick={onClose}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/15 text-slate-400 hover:text-white hover:border-white/30 text-xs transition-colors">
            <X size={13} /> Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ── CAMERA HEALTH CARD ────────────────────────────────────────────────────

const CameraHealthCard = ({ cam }) => {
  const status    = getCameraStatus(cam);
  const alertRate = cam.total_events > 0 ? Math.round((cam.alerts / cam.total_events) * 100) : 0;
  const barW      = Math.min(100, (cam.total_events / 50) * 100); // scale to 50 events max visually

  return (
    <div className={`relative bg-white rounded-xl border ${status.border} p-4 hover:shadow-md transition-all duration-200 group`}>
      {/* Status dot */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${status.dot} ${status.label === "Active" ? "animate-[pulse_2s_infinite]" : ""}`} />
          <span className={`text-[10px] font-bold uppercase tracking-wider ${status.color}`}>{status.label}</span>
        </div>
        {cam.alerts > 0 && (
          <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-red-600 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded-full">
            <AlertTriangle size={9} /> {cam.alerts}
          </span>
        )}
      </div>

      {/* Camera name */}
      <p className="text-sm font-bold text-slate-800 truncate mb-0.5">{cam.camera_name}</p>
      <p className="text-[10px] text-slate-400 mb-3">ID: {cam.camera_id}</p>

      {/* Stats row - Updated to show only Peak and Alerts */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="text-center">
          <p className="text-base font-black text-amber-600">{cam.peak}</p>
          <p className="text-[9px] text-slate-400 uppercase tracking-wide">Peak</p>
        </div>
        <div className="text-center">
          <p className="text-base font-black text-red-600">{cam.alerts}</p>
          <p className="text-[9px] text-slate-400 uppercase tracking-wide">Alerts</p>
        </div>
      </div>

      {/* Activity bar */}
      <div className="mb-1.5">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] text-slate-400">Activity</span>
          <span className="text-[10px] font-bold text-slate-600">{alertRate}% alerts</span>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full flex overflow-hidden" style={{ width: `${barW}%` }}>
            <div className="bg-blue-400 flex-1" />
            {alertRate > 0 && (
              <div className="bg-red-400" style={{ width: `${alertRate}%` }} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── HEATMAP ────────────────────────────────────────────────────────────────

const DetectionHeatmap = ({ reports, cameras }) => {
  const { matrix, cameraNames, maxVal } = useMemo(() => {
    if (!reports?.length || !cameras?.length) return { matrix: {}, cameraNames: [], maxVal: 1 };

    const camNames = cameras.slice(0, 10).map(c => ({
      id: c.camera_id,
      name: (c.camera_name || `Cam ${c.camera_id}`).slice(0, 14)
    }));
    const camIds   = new Set(camNames.map(c => c.id));
    const mat      = {};
    let   mx       = 0;

    camNames.forEach(c => {
      mat[c.id] = {};
      HOURS.forEach(h => { mat[c.id][h] = 0; });
    });

    reports.forEach(r => {
      if (!camIds.has(r.camera_id)) return;
      const h = r.timestampString
        ? r.timestampString.slice(11, 13) + ":00"
        : dayjs(r.timestamp).format("HH:00");
      const val = r.person_count || 0;
      if (mat[r.camera_id]) {
        mat[r.camera_id][h] = (mat[r.camera_id][h] || 0) + val;
        if (mat[r.camera_id][h] > mx) mx = mat[r.camera_id][h];
      }
    });

    return { matrix: mat, cameraNames: camNames, maxVal: mx || 1 };
  }, [reports, cameras]);

  if (!cameraNames.length) return <Empty description="No heatmap data" className="py-10" />;

  return (
    <div className="overflow-x-auto">
      <div style={{ minWidth: 640 }}>
        {/* Hour labels */}
        <div className="flex mb-1.5" style={{ paddingLeft: 112 }}>
          {HOURS.map((h, i) => (
            <div key={h} className="flex-1 text-center">
              {i % 3 === 0 && <span className="text-[9px] text-slate-400 font-medium">{h.slice(0, 2)}</span>}
            </div>
          ))}
        </div>

        {/* Rows */}
        {cameraNames.map(cam => (
          <div key={cam.id} className="flex items-center mb-1 gap-2">
            <div className="w-28 flex-shrink-0">
              <p className="text-[11px] font-semibold text-slate-700 truncate text-right">{cam.name}</p>
            </div>
            <div className="flex flex-1 gap-[2px]">
              {HOURS.map(h => {
                const val   = matrix[cam.id]?.[h] || 0;
                const style = getHeatColor(val, maxVal);
                return (
                  <div
                    key={h}
                    title={`${cam.name} · ${h} · ${val} persons`}
                    className="flex-1 h-6 rounded-[3px] flex items-center justify-center transition-all duration-150 hover:scale-110 hover:z-10 cursor-default"
                    style={{ backgroundColor: style.bg }}
                  >
                    {val > 0 && (
                      <span className="text-[8px] font-bold" style={{ color: style.text }}>
                        {val > 99 ? "99+" : val}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Legend */}
        <div className="flex items-center gap-3 mt-4 justify-end" style={{ paddingRight: 4 }}>
          <span className="text-[10px] text-slate-400">Low</span>
          {["#ECFDF5", "#D1FAE5", "#FEF3C7", "#FED7AA", "#FEE2E2"].map(c => (
            <div key={c} className="w-5 h-3 rounded-sm" style={{ backgroundColor: c }} />
          ))}
          <span className="text-[10px] text-slate-400">High</span>
        </div>
      </div>
    </div>
  );
};

// ── ALERT RATE GAUGE ──────────────────────────────────────────────────────

const AlertGauge = ({ pct }) => {
  const safeP = Math.min(100, Math.max(0, pct || 0));
  const color = safeP > 30 ? "#EF4444" : safeP > 10 ? "#F59E0B" : "#10B981";
  const data  = [{ name: "Alert Rate", value: safeP, fill: color }, { name: "safe", value: 100 - safeP, fill: "#F1F5F9" }];

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-36 h-36">
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart innerRadius="65%" outerRadius="90%" data={data} startAngle={90} endAngle={-270}>
            <RadialBar dataKey="value" cornerRadius={6} background={false}>
              {data.map((d, i) => <Cell key={i} fill={d.fill} />)}
            </RadialBar>
          </RadialBarChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <p className="text-2xl font-black" style={{ color }}>{safeP}%</p>
          <p className="text-[10px] text-slate-400 font-medium">alert rate</p>
        </div>
      </div>
      <p className="text-xs font-semibold mt-1" style={{ color }}>
        {safeP > 30 ? "Critical — Investigate" : safeP > 10 ? "Elevated — Monitor" : "Normal — All Clear"}
      </p>
    </div>
  );
};

// ── TOP ALERT CAMERAS ─────────────────────────────────────────────────────

const TopAlertCameras = ({ cameras }) => {
  const sorted = useMemo(() =>
    [...(cameras || [])].sort((a, b) => b.alerts - a.alerts).slice(0, 6),
    [cameras]
  );
  const maxAlerts = sorted[0]?.alerts || 1;

  if (!sorted.length) return <Empty description="No alert data" className="py-6" />;

  return (
    <div className="space-y-3">
      {sorted.map((cam, i) => {
        const pct    = Math.round((cam.alerts / maxAlerts) * 100);
        const rank   = ["🥇", "🥈", "🥉"][i] || `#${i + 1}`;
        const status = getCameraStatus(cam);
        return (
          <div key={cam.camera_id} className="flex items-center gap-3">
            <span className="text-sm w-5 text-center">{rank}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[12px] font-bold text-slate-700 truncate">{cam.camera_name}</span>
                <span className={`text-[11px] font-bold ${status.color}`}>{cam.alerts} alerts</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-red-400 rounded-full transition-all duration-700"
                  style={{ width: `${pct}%` }} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── DETECTION TIMELINE (Spark) ─────────────────────────────────────────────

const DetectionTimeline = ({ cameras, reports }) => {
  const data = useMemo(() => {
    if (!reports?.length) return [];
    const map = {};
    reports.forEach(r => {
      const slot = r.timestampString
        ? r.timestampString.slice(11, 13) + ":00"
        : dayjs(r.timestamp).format("HH:00");
      if (!map[slot]) map[slot] = { slot, persons: 0, alerts: 0 };
      map[slot].persons += r.person_count || 0;
      if (r.alert) map[slot].alerts++;
    });
    return Object.values(map).sort((a, b) => a.slot.localeCompare(b.slot));
  }, [reports]);

  if (!data.length) return <Empty description="No timeline data" className="py-6" />;

  return (
    <ResponsiveContainer width="100%" height={140}>
      <BarChart data={data} margin={{ left: -20, right: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
        <XAxis dataKey="slot" tick={{ fontSize: 9, fill: "#94A3B8" }} tickLine={false} axisLine={false}
          interval={3} />
        <YAxis tick={{ fontSize: 9, fill: "#94A3B8" }} tickLine={false} axisLine={false} />
        <Tooltip content={<ChartTooltip />} />
        <Bar dataKey="persons" name="Persons" fill="#3D63DD" radius={[3, 3, 0, 0]} maxBarSize={18} />
        <Bar dataKey="alerts"  name="Alerts"  fill="#EF4444" radius={[3, 3, 0, 0]} maxBarSize={10} />
      </BarChart>
    </ResponsiveContainer>
  );
};


// ── GUARD STATUS TIMELINE ──────────────────────────────────────────────────────

const GuardStatusTimeline = ({ data }) => {
  // All hooks must be called before any conditional returns
  
  // Format time for display - preserve UTC time
  const formattedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    return data.map(item => {
      // Parse the UTC timestamp
      const date = new Date(item.timestamp);
      
      // Get UTC hours and minutes to avoid timezone conversion
      const hours = String(date.getUTCHours()).padStart(2, '0');
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      
      return {
        ...item,
        timeLabel: `${hours}:${minutes}`,
        // Color based on status
        color: item.status === 'Present' ? '#10B981' : '#EF4444'
      };
    });
  }, [data]);

  // Filter to show only data from today's date in UTC
  const todayData = useMemo(() => {
    if (!formattedData || formattedData.length === 0) return [];
    
    const today = new Date();
    const todayUTC = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
    const tomorrowUTC = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate() + 1));
    
    return formattedData.filter(item => {
      const itemDate = new Date(item.timestamp);
      return itemDate >= todayUTC && itemDate < tomorrowUTC;
    });
  }, [formattedData]);

  // Find max value for Y axis
  const maxTotal = useMemo(() => {
    if (!todayData || todayData.length === 0) return 1;
    return Math.max(...todayData.map(d => d.total), 1);
  }, [todayData]);

  // Custom Tooltip Component
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0].payload;
    return (
      <div className="bg-slate-900 rounded-xl px-4 py-3 shadow-2xl border border-white/10 min-w-[140px]">
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">
          {data.timeLabel} UTC
        </p>
        <div className="space-y-1">
          <p className="flex items-center gap-2 text-xs">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="text-slate-300">Present:</span>
            <strong className="text-white">{data.present}</strong>
          </p>
          <p className="flex items-center gap-2 text-xs">
            <span className="w-2 h-2 rounded-full bg-red-500" />
            <span className="text-slate-300">Absent:</span>
            <strong className="text-white">{data.absent}</strong>
          </p>
          <p className="flex items-center gap-2 text-xs pt-1 border-t border-white/10">
            <span className="text-slate-300">Status:</span>
            <strong className={data.status === 'Present' ? 'text-emerald-400' : 'text-red-400'}>
              {data.status} ({data.percentage}%)
            </strong>
          </p>
        </div>
      </div>
    );
  };

  // Conditional returns AFTER all hooks
  if (!data || data.length === 0) {
    return (
      <div className="w-full py-4 text-center text-slate-400 text-sm">
        <Shield size={20} className="mx-auto mb-2 text-slate-300" />
        No guard status data available for today
      </div>
    );
  }

  if (todayData.length === 0) {
    return (
      <div className="w-full py-4 text-center text-slate-400 text-sm">
        <Shield size={20} className="mx-auto mb-2 text-slate-300" />
        No guard status data available for today
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex items-center gap-2 mb-3">
        <Shield size={16} className="text-blue-600" />
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
          Guard Presence by 15-min Intervals (UTC)
        </span>
        <div className="flex items-center gap-3 ml-auto text-[10px] font-semibold">
          <span className="flex items-center gap-1 text-emerald-600">
            <span className="w-2 h-2 rounded-full bg-emerald-500" /> Present
          </span>
          <span className="flex items-center gap-1 text-red-600">
            <span className="w-2 h-2 rounded-full bg-red-500" /> Absent
          </span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={120}>
        <BarChart data={todayData} margin={{ left: -20, right: 0, top: 5, bottom: 5 }}>
          <XAxis 
            dataKey="timeLabel" 
            tick={{ fontSize: 9, fill: "#94A3B8" }} 
            tickLine={false} 
            axisLine={false}
            interval={Math.floor(todayData.length / 10)}
          />
          <YAxis 
            tick={{ fontSize: 9, fill: "#94A3B8" }} 
            tickLine={false} 
            axisLine={false}
            domain={[0, maxTotal + 1]}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="total" radius={[3, 3, 0, 0]} maxBarSize={24}>
            {todayData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div className="flex justify-center gap-4 mt-2 text-[10px] text-slate-400">
        <span>🟢 = Guard Present</span>
        <span>🔴 = Guard Absent</span>
        <span>Bar height = total checks in interval</span>
        <span>⏰ UTC Time</span>
      </div>
    </div>
  );
};

// ── LIVE STATUS STRIP ──────────────────────────────────────────────────────

const LiveStatusStrip = ({ cameras }) => {
  if (!cameras?.length) return null;
  return (
    <div className="flex gap-3 overflow-x-auto pb-1">
      {cameras.map(cam => {
        const status = getCameraStatus(cam);
        return (
          <div key={cam.camera_id}
            className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-xl border ${status.border} ${status.bg} min-w-[140px]`}>
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${status.dot}
              ${status.label === "Active" || status.label === "Critical" ? "animate-[pulse_2s_infinite]" : ""}`} />
            <div className="min-w-0">
              <p className={`text-[11px] font-bold truncate ${status.color}`}>{cam.camera_name}</p>
              <p className="text-[9px] text-slate-400">{cam.alerts} alerts</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── MAIN DASHBOARD ──────────────────────────────────────────────────────────

export default function Dashboard() {
  const [loading,       setLoading]       = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [lightboxAlert, setLightboxAlert] = useState(null);
  const [autoRefresh,   setAutoRefresh]   = useState(true);
  const [lastUpdated,   setLastUpdated]   = useState(null);
  const [isGuardPresent, setIsGuardPresent] = useState(false);
  const [guardHistory, setGuardHistory] = useState([]);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get("/tanseeq/dashboard/full");
      if (response.data.success) {
        setDashboardData(response.data.data);
        setLastUpdated(new Date());
      }
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchGuardStatus = useCallback(async () => {
    try {
      const response = await axios.get("/fetch-tanseeq-guardstatus");
      if (response.data.success) {
        // Just set the guardPresent value from the response
        setIsGuardPresent(response.data.data.guardPresent);
      }
    } catch (err) {
      console.error("Guard status fetch error:", err);
    }
  }, []);

  const fetchGuardHistory = useCallback(async () => {
    try {
      const response = await axios.get("/fetch-tanseeq-guardstatus-history");
      if (response.data.success) {
        setGuardHistory(response.data.data);
      }
    } catch (err) {
      console.error("Guard history fetch error:", err);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    fetchGuardStatus();
    fetchGuardHistory();
    if (!autoRefresh) return;
    const id = setInterval(() => {
      fetchDashboardData();
      fetchGuardStatus();
      fetchGuardHistory();
    }, 60000);
    return () => clearInterval(id);
  }, [fetchDashboardData, fetchGuardStatus, autoRefresh]);

  const chartData = useMemo(() => {
    if (!dashboardData?.reports) return { hourlyData: [], cameraData: [], densityData: [] };
    const reports = dashboardData.reports;

    const hMap = {};
    reports.forEach(r => {
      const h = r.timestampString
        ? r.timestampString.slice(11, 13) + ":00"
        : dayjs(r.timestamp).format("HH:00");

      if (!hMap[h]) hMap[h] = { hour: h, persons: 0, alerts: 0 };
      hMap[h].persons += r.personsVisible || r.person_count || 0;
      if (r.alert) hMap[h].alerts++;
    });
    const hourlyData = Object.values(hMap).sort((a, b) => a.hour.localeCompare(b.hour));

    const cMap = {};
    reports.forEach(r => {
      const id = r.camera_id;
      if (!cMap[id]) cMap[id] = { camera_name: r.camera_name || `Cam ${id}`, total: 0, alerts: 0 };
      cMap[id].total++;
      if (r.alert) cMap[id].alerts++;
    });
    const cameraData = Object.values(cMap).sort((a, b) => b.total - a.total).slice(0, 8);

    const dMap = { "Empty": 0, "Low (1–3)": 0, "Medium (4–6)": 0, "High (7–10)": 0, "Critical (10+)": 0 };
    reports.forEach(r => {
      const c = r.personsVisible || r.person_count || 0;
      if (c === 0) dMap["Empty"]++;
      else if (c <= 3)  dMap["Low (1–3)"]++;
      else if (c <= 6)  dMap["Medium (4–6)"]++;
      else if (c <= 10) dMap["High (7–10)"]++;
      else              dMap["Critical (10+)"]++;
    });
    const densityData = Object.entries(dMap).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value }));

    return { hourlyData, cameraData, densityData };
  }, [dashboardData]);

  if (loading && !dashboardData) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Spin size="large" tip="Loading dashboard…" />
      </div>
    );
  }
  if (!dashboardData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Empty description="No dashboard data available" />
        <button onClick={fetchDashboardData}
          className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors">
          Retry
        </button>
      </div>
    );
  }

  const { fleet, statistics, cameras, recent_alerts, reports } = dashboardData;

  return (
    <div className="min-h-screen bg-slate-50">
      <style>{`
        @keyframes fadeIn  { from { opacity: 0 }                                    to { opacity: 1 } }
        @keyframes slideUp { from { opacity:0; transform:translateY(16px) scale(0.98) } to { opacity:1; transform:translateY(0) scale(1) } }
        @keyframes pulse   { 0%,100%{opacity:1} 50%{opacity:0.3} }
      `}</style>

      <div className="max-w-[1440px] mx-auto px-6 py-6 space-y-6">

        {/* ── HEADER ── */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-200">
              <ShieldAlert size={22} className="text-white" strokeWidth={2.1} />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-black text-slate-900 tracking-tight leading-none">
                  Analytics Dashboard
                </h1>
                <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded-full
                  ${autoRefresh ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${autoRefresh ? "bg-emerald-500 animate-[pulse_2s_infinite]" : "bg-slate-400"}`} />
                  {autoRefresh ? "LIVE" : "PAUSED"}
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                {lastUpdated && ` · Updated ${dayjs(lastUpdated).format("HH:mm:ss")}`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setAutoRefresh(r => !r)}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold border transition-all
                ${autoRefresh
                  ? "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                  : "bg-slate-100 border-slate-200 text-slate-500 hover:bg-slate-200"}`}>
              <span className={`w-2 h-2 rounded-full ${autoRefresh ? "bg-emerald-500" : "bg-slate-400"}`} />
              {autoRefresh ? "Auto-refresh on" : "Auto-refresh off"}
            </button>
            <button
              onClick={fetchDashboardData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 disabled:opacity-60 transition-colors shadow-sm">
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>

        {/* ── LIVE STATUS STRIP ── */}
        <div className="bg-white rounded-2xl border border-slate-200 px-5 py-4 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Radio size={13} className="text-emerald-500 animate-[pulse_2s_infinite]" />
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Live Camera Status</span>
          </div>
          <LiveStatusStrip cameras={cameras} />
        </div>

        {/* ── KPI STRIP - UPDATED: Removed Total Events and Total People ── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-4">
          <KPICard title="Peak Occupancy" value={statistics.peak_person_count}  icon={TrendingUp}    colorClass="text-amber-600"  bgClass="bg-amber-50"  sub="Max persons at once" />
          <KPICard title="Total Alerts"   value={fleet.total_alerts}            icon={AlertTriangle} colorClass="text-red-500"    bgClass="bg-red-50" />
          <KPICard title="Active Cameras" value={fleet.active_cameras || fleet.total_cameras} icon={Camera} colorClass="text-violet-600" bgClass="bg-violet-50" sub={`of ${fleet.total_cameras} cameras`} />
        </div>

        {/* ── ADDITIONAL ALERT CARDS ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <MetricCard 
            title="Intrusion Alerts" 
            value={statistics.intrusion_alerts || 0} 
            icon={ShieldAlert} 
            colorClass="text-red-500" 
            bgClass="bg-red-50"
          />
          
          <MetricCard 
            title="Overcrowding Alerts" 
            value={statistics.overcrowding_alerts || 0} 
            icon={Users} 
            colorClass="text-amber-500" 
            bgClass="bg-amber-50" 
          />
          
          <MetricCard
            title="Kitchen Congestion"
            value={statistics.kitchen_congestion || 0}
            icon={Layers}
            colorClass="text-emerald-500"
            bgClass="bg-emerald-50"
            sub="Kitchen (ID:2)"
          />
          
          <MetricCard 
            title="Smoke &amp; Fire" 
            value={0} 
            icon={Activity} 
            colorClass="text-orange-500"
            bgClass="bg-orange-50" 
            sub="No threats detected"
          />
        </div>

        {/* ── NEW: ADDITIONAL SECURITY METRICS ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <MetricCard 
            title="Loitering" 
            value={0} 
            icon={AlertTriangle} 
            colorClass="text-purple-500" 
            bgClass="bg-purple-50" 
            sub="No loitering detected"
          />
          
          <MetricCard 
            title="Fall Detection" 
            value={0} 
            icon={Users} 
            colorClass="text-pink-500" 
            bgClass="bg-pink-50" 
            sub="No falls detected"
          />
          
          <MetricCard 
            title="Parking Security" 
            value={isGuardPresent ? 'Present' : 'Absent'} 
            icon={isGuardPresent ? CheckCircle : X} 
            colorClass={isGuardPresent ? "text-green-500" : "text-red-500"} 
            bgClass={isGuardPresent ? "bg-green-50" : "bg-red-50"}
            sub="On Site"
          />

          <MetricCard 
            title="Late Night Alerts" 
            value={statistics.late_night_alerts || 0} 
            icon={Clock} 
            colorClass="text-indigo-500" 
            bgClass="bg-indigo-50"
            sub="Parking (ID:4)"
          />
        </div>

        {/* ── DETECTION TIMELINE + ALERT GAUGE + TOP ALERT CAMERAS ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Detection Timeline */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <SectionHeader
              icon={Activity}
              iconClass="text-blue-600"
              iconBg="bg-blue-50"
              title="Detection Timeline"
              sub="Persons & alerts per hour"
            />
            <DetectionTimeline reports={reports} />
          </div>

          {/* Right column: gauge + top cameras */}
          <div className="flex flex-col gap-5">
            {/* Alert Gauge */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm flex flex-col items-center">
              <SectionHeader
                icon={Target}
                iconClass="text-red-500"
                iconBg="bg-red-50"
                title="System Alert Rate"
                sub="Overall health indicator"
              />
              <AlertGauge pct={statistics.alert_percentage} />
            </div>

            {/* Busiest camera callout */}
            {fleet.busiest_camera && (
              <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-5 text-white shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Zap size={14} className="text-blue-200" />
                  <span className="text-[10px] font-bold uppercase tracking-wider text-blue-200">Busiest Camera</span>
                </div>
                <p className="text-lg font-black leading-tight">{fleet.busiest_camera}</p>
                <p className="text-xs text-blue-200 mt-1">Highest detection volume today</p>
              </div>
            )}
          </div>
        </div>

       {/* ── GUARD STATUS TIMELINE ── */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <SectionHeader
            icon={Shield}
            iconClass="text-blue-600"
            iconBg="bg-blue-50"
            title="Guard Attendance Timeline"
            sub="15-minute intervals - showing guard presence throughout the day"
          />
          <GuardStatusTimeline data={guardHistory} />
        </div>

        {/* ── HEATMAP ── */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <SectionHeader
            icon={Thermometer}
            iconClass="text-orange-500"
            iconBg="bg-orange-50"
            title="Detection Heatmap"
            sub="Camera × hour — persons detected (darker = more activity)"
          />
          <DetectionHeatmap reports={reports} cameras={cameras} />
        </div>

        {/* ── CAMERA HEALTH GRID ── */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <SectionHeader
            icon={Eye}
            iconClass="text-violet-600"
            iconBg="bg-violet-50"
            title="Camera Health Overview"
            sub="Real-time status per camera"
            right={
              <div className="flex items-center gap-3 text-[10px] font-semibold">
                {[["bg-emerald-500","Active"],["bg-amber-500","Warning"],["bg-red-500","Critical"],["bg-slate-300","Idle"]].map(([c, l]) => (
                  <span key={l} className="flex items-center gap-1 text-slate-500">
                    <span className={`w-2 h-2 rounded-full ${c}`} /> {l}
                  </span>
                ))}
              </div>
            }
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {cameras?.map((cam, i) => <CameraHealthCard key={cam.camera_id || i} cam={cam} />)}
            {!cameras?.length && <Empty description="No cameras" className="col-span-5 py-10" />}
          </div>
        </div>

        {/* ── TOP OFFENDERS + DENSITY PIE + RECENT ALERTS ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">

          {/* Top Alert Cameras */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <SectionHeader
              icon={AlertTriangle}
              iconClass="text-red-500"
              iconBg="bg-red-50"
              title="Top Alert Cameras"
              sub="Ranked by alert count"
            />
            <TopAlertCameras cameras={cameras} />
          </div>

          {/* Density Pie */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <SectionHeader
              icon={BarChart3}
              iconClass="text-violet-600"
              iconBg="bg-violet-50"
              title="Density Distribution"
              sub="People count breakdown"
            />
            {chartData.densityData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={chartData.densityData} cx="50%" cy="50%"
                      innerRadius={50} outerRadius={72} paddingAngle={3} dataKey="value"
                      label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                      labelLine={{ stroke: "#E4E7F0", strokeWidth: 1 }}>
                      {chartData.densityData.map((_, i) => (
                        <Cell key={i} fill={PALETTE[i % PALETTE.length]} stroke="white" strokeWidth={2} />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                {/* Legend */}
                <div className="grid grid-cols-2 gap-1 mt-2">
                  {chartData.densityData.map((d, i) => (
                    <div key={d.name} className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: PALETTE[i % PALETTE.length] }} />
                      <span className="text-[10px] text-slate-500 truncate">{d.name}</span>
                      <span className="text-[10px] font-bold text-slate-700 ml-auto">{d.value}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <Empty description="No density data" className="py-10" />
            )}
          </div>

          {/* Recent Alerts */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
            <div className="px-6 pt-6 pb-4 border-b border-slate-100">
              <SectionHeader
                icon={Bell}
                iconClass="text-red-500"
                iconBg="bg-red-50"
                title="Recent Alerts"
                sub="Latest detection events"
                right={
                  <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-red-50 text-red-600">
                    {recent_alerts?.length || 0} NEW
                  </span>
                }
              />
            </div>
            <div className="flex-1 overflow-y-auto max-h-[340px]">
              {recent_alerts?.length > 0 ? (
                recent_alerts.slice(0, 8).map((a, i) => (
                  <AlertRow key={a._id || i} alert={a} onClick={setLightboxAlert} />
                ))
              ) : (
                <div className="flex flex-col items-center justify-center py-12 gap-2 text-slate-400">
                  <CheckCircle size={32} className="text-emerald-400" />
                  <p className="text-sm font-medium text-slate-500">No alerts to show</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── CAMERA STATISTICS TABLE - UPDATED: Removed Events, Total People, Alert Rate ── */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
            <SectionHeader
              icon={BarChart3}
              iconClass="text-blue-600"
              iconBg="bg-blue-50"
              title="Camera Statistics"
              sub="Full breakdown by camera"
            />
            <span className="text-xs text-slate-400">{cameras?.length || 0} cameras</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  {["Camera", "Status", "Peak", "Alerts"].map(h => (
                    <th key={h} className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-slate-400">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cameras?.length > 0 ? cameras.map((cam, i) => {
                  const status = getCameraStatus(cam);
                  return (
                    <tr key={cam.camera_id || i}
                      className="border-b border-slate-50 hover:bg-slate-50/70 transition-colors last:border-b-0">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                            <Camera size={13} className="text-blue-600" />
                          </div>
                          <div>
                            <p className="font-bold text-slate-800 text-[13px]">{cam.camera_name || cam.camera_id}</p>
                            <p className="text-[10px] text-slate-400">ID: {cam.camera_id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full border ${status.bg} ${status.color} ${status.border}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                          {status.label}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <span className="font-bold text-amber-600">{cam.peak}</span>
                      </td>
                      <td className="px-5 py-3">
                        {cam.alerts > 0 ? (
                          <span className="inline-flex items-center gap-1 text-xs font-bold text-red-600 bg-red-50 px-2.5 py-1 rounded-full">
                            <AlertTriangle size={11} /> {cam.alerts}
                          </span>
                        ) : (
                          <span className="text-slate-300 font-medium">—</span>
                        )}
                      </td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td colSpan={4} className="px-5 py-12 text-center text-slate-400 text-sm">
                      No camera data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>

      <Lightbox open={!!lightboxAlert} onClose={() => setLightboxAlert(null)} alert={lightboxAlert} />
    </div>
  );
}